import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

/**
 * Exact permission strings from openapi/generated/route_policy.gen.go — the
 * only authority. A control the session cannot exercise is never rendered.
 */
export const PURCHASING_PERMISSIONS = {
  purchaseOrderRead: "purchasing.purchase_order.read",
  purchaseOrderCreate: "purchasing.purchase_order.create",
  purchaseOrderUpdate: "purchasing.purchase_order.update",
  purchaseOrderDelete: "purchasing.purchase_order.delete",
  purchaseOrderSubmit: "purchasing.purchase_order.submit",
  purchaseOrderSend: "purchasing.purchase_order.send",
  purchaseOrderClose: "purchasing.purchase_order.close",
  transmissionRead: "purchasing.purchase_order.transmission.read",
  grnRead: "purchasing.grn.read",
  grnPost: "purchasing.grn.post",
  landedCostRead: "purchasing.landed_cost.read",
  landedCostCreate: "purchasing.landed_cost.create",
  landedCostUpdate: "purchasing.landed_cost.update",
  landedCostDelete: "purchasing.landed_cost.delete",
  landedCostPost: "purchasing.landed_cost.post",
  supplierItemRead: "purchasing.supplier_item.read",
  supplierItemCreate: "purchasing.supplier_item.create",
  supplierItemUpdate: "purchasing.supplier_item.update",
  supplierItemDelete: "purchasing.supplier_item.delete",
  reorderSuggestionRead: "purchasing.reorder_suggestion.read",
  reorderSuggestionConvert: "purchasing.reorder_suggestion.convert",
  reorderSuggestionDismiss: "purchasing.reorder_suggestion.dismiss",
  reorderSuggestionCalculate: "purchasing.reorder_suggestion.calculate",
  configRead: "purchasing.config.read",
  // Cross-domain reads the screens depend on for names and pickers.
  partyRead: "partners.party.read",
  variantRead: "catalog.variant.read",
  barcodeRead: "catalog.barcode.read",
  uomRead: "catalog.uom.read",
  warehouseRead: "inventory.warehouse.read",
  batchRead: "inventory.batch.read",
} as const;

export type PurchasingPermission =
  (typeof PURCHASING_PERMISSIONS)[keyof typeof PURCHASING_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function PurchasingPermissionBoundary({
  permission,
  children,
}: {
  permission: PurchasingPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("purchasing")) return <ModuleBoundary app="purchasing">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="purchasing-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="purchasing-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-xs font-semibold tracking-[0.18em] text-destructive">
              {t("purchasing.permissions.code", "403 · ACCESS CONTROL")}
            </p>
            <h1 id="purchasing-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("purchasing.permissions.title", "Purchasing access restricted")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
